import { useEffect, useRef, useState } from "react";
import {
  BankAccountCreateRequest,
  BankAccountResponse,
  BankAccountUpdateRequest,
} from "api/main/data-contracts";
import { createApi } from "util/ApiCreator";
import { BankAccountController } from "api/main/BankAccountController";
import { useTestMode } from "context/TestModeContext";
import { mockBankAccounts } from "data/mockData";
import { adminTheme, statusColors } from "theme/adminTheme";
import {
  Box,
  Container,
  Typography,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  LinearProgress,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControlLabel,
  Switch,
} from "@mui/material";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import RefreshIcon from "@mui/icons-material/Refresh";

export default function BankAccountPage() {
  const api = useRef(createApi("bankaccount") as BankAccountController);
  const { isTestMode } = useTestMode();
  const [bankAccounts, setBankAccounts] = useState<BankAccountResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingAccount, setEditingAccount] = useState<BankAccountResponse | null>(null);
  const [formData, setFormData] = useState({
    creditorIban: "",
    creditorName: "",
    currency: "EUR" as "EUR" | "USD" | "GBP",
    approved: false,
  });

  useEffect(() => {
    if (isTestMode) {
      setBankAccounts(mockBankAccounts);
      setLoading(false);
    } else {
      handleDataFetch();
    }
  }, [isTestMode]);

  async function handleDataFetch() {
    setLoading(true);
    try {
      const data = await api.current.findAll();
      setBankAccounts(data || []);
    } catch (error) {
      console.error("Error fetching bank accounts:", error);
    } finally {
      setLoading(false);
    }
  }

  const openCreateDialog = () => {
    setEditingAccount(null);
    setFormData({ creditorIban: "", creditorName: "", currency: "EUR", approved: false });
    setDialogOpen(true);
  };

  const openEditDialog = (account: BankAccountResponse) => {
    setEditingAccount(account);
    setFormData({
      creditorIban: account.creditorIban || "",
      creditorName: account.creditorName || "",
      currency: account.currency || "EUR",
      approved: account.approved || false,
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (isTestMode) {
      setDialogOpen(false);
      return;
    }

    try {
      if (editingAccount?.id) {
        await api.current.update({
          id: editingAccount.id,
          ...formData,
        } as BankAccountUpdateRequest);
      } else {
        await api.current.create(formData as BankAccountCreateRequest);
      }
      await handleDataFetch();
      setDialogOpen(false);
    } catch (error) {
      console.error("Error saving bank account:", error);
    }
  };

  const handleDelete = async (account: BankAccountResponse) => {
    if (isTestMode || !account.id) return;

    if (window.confirm("Ar tikrai norite isstrinti si saskaita?")) {
      try {
        await api.current.delete(account.id);
        await handleDataFetch();
      } catch (error) {
        console.error("Error deleting bank account:", error);
      }
    }
  };

  return (
    <Box sx={{ bgcolor: adminTheme.colors.backgroundAlt, minHeight: "100vh" }}>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 4,
          }}
        >
          <Box>
            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                mb: 1,
                color: adminTheme.colors.textPrimary,
              }}
            >
              Banko saskaitos
            </Typography>
            <Typography
              variant="body"
              sx={{ color: adminTheme.colors.textSecondary }}
            >
              Valdykite banko saskaitas mokejimams
            </Typography>
          </Box>
          <Box sx={{ display: "flex", gap: 2 }}>
            <Tooltip title="Atnaujinti">
              <IconButton
                onClick={() => !isTestMode && handleDataFetch()}
                sx={{
                  bgcolor: adminTheme.colors.background,
                  border: `1px solid ${adminTheme.colors.border}`,
                }}
              >
                <RefreshIcon />
              </IconButton>
            </Tooltip>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openCreateDialog}
              sx={{
                background: adminTheme.gradients.primary,
                "&:hover": { background: adminTheme.gradients.primaryHover },
              }}
            >
              Nauja saskaita
            </Button>
          </Box>
        </Box>

        {loading && <LinearProgress sx={{ mb: 3 }} />}

        {/* Stats */}
        <Box sx={{ display: "flex", gap: 3, mb: 4, flexWrap: "wrap" }}>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Viso saskaitu
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.textPrimary }}
            >
              {bankAccounts.length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Patvirtintos
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.success }}
            >
              {bankAccounts.filter((b) => b.approved).length}
            </Typography>
          </Card>
          <Card
            sx={{
              flex: 1,
              minWidth: 180,
              p: 3,
              borderRadius: adminTheme.borderRadius.lg,
              border: `1px solid ${adminTheme.colors.border}`,
              boxShadow: "none",
            }}
          >
            <Typography
              variant="body2"
              sx={{ color: adminTheme.colors.textMuted, mb: 1 }}
            >
              Laukiancios
            </Typography>
            <Typography
              variant="h3"
              sx={{ fontWeight: 700, color: adminTheme.colors.warning }}
            >
              {bankAccounts.filter((b) => !b.approved).length}
            </Typography>
          </Card>
        </Box>

        {/* Table */}
        <Card
          sx={{
            borderRadius: adminTheme.borderRadius.lg,
            border: `1px solid ${adminTheme.colors.border}`,
            boxShadow: "none",
            overflow: "hidden",
          }}
        >
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: adminTheme.colors.backgroundAlt,
                    "& th": {
                      fontWeight: 600,
                      color: adminTheme.colors.textPrimary,
                      borderBottom: `2px solid ${adminTheme.colors.border}`,
                      py: 2,
                    },
                  }}
                >
                  <TableCell>Saskaitos pavadinimas</TableCell>
                  <TableCell>IBAN</TableCell>
                  <TableCell>Valiuta</TableCell>
                  <TableCell>Busena</TableCell>
                  <TableCell align="right">Veiksmai</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {bankAccounts.length === 0 && !loading ? (
                  <TableRow>
                    <TableCell colSpan={5}>
                      <Box
                        sx={{
                          textAlign: "center",
                          py: 6,
                          color: adminTheme.colors.textMuted,
                        }}
                      >
                        <AccountBalanceIcon
                          sx={{ fontSize: 48, mb: 2, opacity: 0.5 }}
                        />
                        <Typography>Nera banko saskaitu</Typography>
                        <Button
                          variant="outlined"
                          startIcon={<AddIcon />}
                          onClick={openCreateDialog}
                          sx={{ mt: 2 }}
                        >
                          Prideti saskaita
                        </Button>
                      </Box>
                    </TableCell>
                  </TableRow>
                ) : (
                  bankAccounts.map((account) => (
                    <TableRow
                      key={account.id}
                      sx={{
                        transition: adminTheme.transitions.fast,
                        "&:hover": {
                          bgcolor: "rgba(99, 102, 241, 0.04)",
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                          <Box
                            sx={{
                              width: 40,
                              height: 40,
                              borderRadius: adminTheme.borderRadius.sm,
                              bgcolor: account.approved
                                ? "rgba(16, 185, 129, 0.1)"
                                : adminTheme.colors.backgroundAlt,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: account.approved
                                ? adminTheme.colors.success
                                : adminTheme.colors.textMuted,
                            }}
                          >
                            <AccountBalanceIcon sx={{ fontSize: 20 }} />
                          </Box>
                          <Typography sx={{ fontWeight: 500 }}>
                            {account.creditorName}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          sx={{ fontFamily: "monospace" }}
                        >
                          {account.creditorIban}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={account.currency}
                          size="small"
                          sx={{
                            bgcolor: adminTheme.colors.backgroundAlt,
                            color: adminTheme.colors.textSecondary,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={account.approved ? "Patvirtinta" : "Laukia"}
                          size="small"
                          sx={{
                            bgcolor: account.approved
                              ? statusColors.active.bg
                              : statusColors.pending.bg,
                            color: account.approved
                              ? statusColors.active.text
                              : statusColors.pending.text,
                            fontWeight: 600,
                          }}
                        />
                      </TableCell>
                      <TableCell align="right">
                        <Tooltip title="Redaguoti">
                          <IconButton
                            size="small"
                            onClick={() => openEditDialog(account)}
                          >
                            <EditIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Istrinti">
                          <IconButton
                            size="small"
                            onClick={() => handleDelete(account)}
                            sx={{ color: adminTheme.colors.error }}
                          >
                            <DeleteIcon sx={{ fontSize: 18 }} />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
          <DialogTitle>
            {editingAccount ? "Redaguoti saskaita" : "Nauja saskaita"}
          </DialogTitle>
          <DialogContent>
            <Box sx={{ pt: 2, display: "flex", flexDirection: "column", gap: 2 }}>
              <TextField
                label="Saskaitos pavadinimas"
                value={formData.creditorName}
                onChange={(e) =>
                  setFormData({ ...formData, creditorName: e.target.value })
                }
                fullWidth
              />
              <TextField
                label="IBAN"
                value={formData.creditorIban}
                onChange={(e) =>
                  setFormData({ ...formData, creditorIban: e.target.value })
                }
                fullWidth
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.approved}
                    onChange={(e) =>
                      setFormData({ ...formData, approved: e.target.checked })
                    }
                  />
                }
                label="Patvirtinta"
              />
            </Box>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setDialogOpen(false)}>Atsaukti</Button>
            <Button
              onClick={handleSave}
              variant="contained"
              sx={{
                background: adminTheme.gradients.primary,
                "&:hover": { background: adminTheme.gradients.primaryHover },
              }}
            >
              Issaugoti
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  );
}
